import { useState, type ReactNode } from 'react';
import { Eye, MousePointerClick, Percent, Trophy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  useRunSeoSearchConsole,
  useSeoSearchConsoleQuery,
  useSetSeoDigest,
  type ApiGscDay,
  type ApiGscProperty,
  type ApiGscRow,
  type ApiGscTotals,
} from '@/client/api/marketing/seo';
import { EmptyState } from './components/marketing-ui';
import { MarketingPageShell } from './components/MarketingPageShell';
import {
  NEUTRAL_SERIES,
  SITE_COLOR,
  SeoError,
  SeoKpi,
  SeoKpiRow,
  SeoNote,
  SeoPanel,
  SeoTrend,
  TD,
  TH,
  TR,
  fmtNumber,
} from './components/seo-ui';

const pct = (n: number | null | undefined) => (n == null ? '—' : `${(n * 100).toFixed(1)}%`);

/** "+12% on the previous 28 days", or nothing when there is no previous snapshot. */
function delta(now: number | null | undefined, before: number | null | undefined) {
  if (now == null || !before) return undefined;
  const change = Math.round(((now - before) / before) * 100);
  return `${change >= 0 ? '+' : ''}${change}% on the previous snapshot`;
}

/** Daily rows summed into weeks (starting Monday): a day of Search Console data is too noisy to read. */
function weekly(days: ApiGscDay[]) {
  const weeks = new Map<string, { week: string; live: number; staging: number; clicks: number }>();
  for (const d of days) {
    const date = new Date(`${d.date}T00:00:00Z`);
    date.setUTCDate(date.getUTCDate() - ((date.getUTCDay() + 6) % 7));
    const week = date.toISOString().slice(0, 10);
    const w = weeks.get(week) ?? { week, live: 0, staging: 0, clicks: 0 };
    w.live += Math.max(0, d.impressions - d.staging_impressions);
    w.staging += d.staging_impressions;
    w.clicks += d.clicks;
    weeks.set(week, w);
  }
  return [...weeks.values()].sort((a, b) => a.week.localeCompare(b.week));
}

function Trend({ days }: { days: ApiGscDay[] }) {
  const weeks = weekly(days);
  if (weeks.length < 2) return null;
  return (
    <div className="grid gap-4 xl:grid-cols-3">
      <SeoPanel
        title="Impressions per week"
        subtitle="Staging should shrink to nothing now that it is noindex; the live site should take its place"
        className="xl:col-span-2"
      >
        <SeoTrend
          data={weeks}
          xKey="week"
          series={[
            { key: 'live', name: 'Live site', color: SITE_COLOR.feelyfeely, kind: 'bar', stack: 'impressions' },
            { key: 'staging', name: 'staging.feelyfeely.com', color: NEUTRAL_SERIES, kind: 'bar', stack: 'impressions' },
          ]}
        />
      </SeoPanel>
      <SeoPanel title="Clicks per week" subtitle="Both hosts">
        <SeoTrend data={weeks} xKey="week" small series={[{ key: 'clicks', name: 'Clicks', color: SITE_COLOR.feelyfeely }]} />
      </SeoPanel>
    </div>
  );
}

function siteLabel(site: string) {
  return site.replace(/^sc-domain:/, '').replace(/^https?:\/\//, '').replace(/\/$/, '');
}

function RowsTable({
  rows,
  isPage,
  extra,
}: {
  rows: ApiGscRow[];
  isPage?: boolean;
  extra?: { label: string; render: (row: ApiGscRow) => ReactNode };
}) {
  if (rows.length === 0) return <p className="px-4 py-3 text-sm text-gray-500">Nothing here this time.</p>;
  return (
    <table className="w-full text-sm">
      <thead>
        <tr>
          <th className={TH}>{isPage ? 'Page' : 'Query'}</th>
          <th className={cn(TH, 'text-right')}>Clicks</th>
          <th className={cn(TH, 'text-right')}>Impressions</th>
          <th className={cn(TH, 'text-right')}>CTR</th>
          <th className={cn(TH, 'text-right')}>Position</th>
          {extra && <th className={cn(TH, 'text-right')}>{extra.label}</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key} className={TR}>
            <td className={cn(TD, 'max-w-md truncate text-gray-900')} title={r.key}>
              {isPage ? r.key.replace(/^https?:\/\/[^/]+/, '') || '/' : r.key}
            </td>
            <td className={cn(TD, 'text-right tabular-nums')}>{fmtNumber(r.clicks)}</td>
            <td className={cn(TD, 'text-right tabular-nums')}>{fmtNumber(r.impressions)}</td>
            <td className={cn(TD, 'text-right tabular-nums')}>{pct(r.ctr)}</td>
            <td className={cn(TD, 'text-right tabular-nums')}>{r.position}</td>
            {extra && <td className={cn(TD, 'text-right')}>{extra.render(r)}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Totals({ now, before, loading }: { now?: ApiGscTotals; before?: ApiGscTotals | null; loading: boolean }) {
  return (
    <SeoKpiRow>
      <SeoKpi label="Clicks" value={now?.clicks} icon={MousePointerClick} hint={delta(now?.clicks, before?.clicks)} loading={loading} />
      <SeoKpi
        label="Impressions"
        value={now?.impressions}
        icon={Eye}
        hint={delta(now?.impressions, before?.impressions)}
        loading={loading}
      />
      <SeoKpi label="Click-through rate" value={pct(now?.ctr)} icon={Percent} hint="Clicks per impression" loading={loading} />
      <SeoKpi
        label="Average position"
        value={now?.position ?? undefined}
        icon={Trophy}
        hint={now ? `Across ${fmtNumber(now.queries)} queries, weighted by impressions` : undefined}
        loading={loading}
      />
    </SeoKpiRow>
  );
}

function Property({ p, loading }: { p: ApiGscProperty; loading: boolean }) {
  if (!p.snapshot) {
    return <EmptyState label="No snapshot for this property yet. Pull it now, or wait for Monday's run." />;
  }
  const movers = p.movers ?? [];
  return (
    <div className="space-y-4">
      <Totals now={p.totals} before={p.previous_totals} loading={loading} />

      <Trend days={p.trend ?? []} />

      <SeoPanel title="Top queries" subtitle="What people searched when they found us, by clicks" flush>
        <RowsTable rows={p.top_queries ?? []} />
      </SeoPanel>

      <div className="grid gap-4 xl:grid-cols-2">
        <SeoPanel
          title="Page two"
          subtitle="Average position 8 to 20: the queries closest to page one, most impressions first"
          flush
        >
          <RowsTable rows={p.striking_distance ?? []} />
        </SeoPanel>
        <SeoPanel
          title="Seen but not clicked"
          subtitle="On page one with under 1% clicks: fix the title or description"
          flush
        >
          <RowsTable rows={p.low_ctr ?? []} />
        </SeoPanel>
      </div>

      {movers.length > 0 && (
        <SeoPanel title="Movers" subtitle="Biggest position changes since the previous snapshot" flush>
          <RowsTable
            rows={movers}
            extra={{
              label: 'Change',
              render: (r) => {
                const change = movers.find((m) => m.key === r.key)?.change ?? 0;
                return (
                  <span className={cn('tabular-nums', change > 0 ? 'text-emerald-700' : 'text-rose-700')}>
                    {change > 0 ? `up ${change}` : `down ${-change}`}
                  </span>
                );
              },
            }}
          />
        </SeoPanel>
      )}

      {(p.tracked?.length ?? 0) > 0 && (
        <SeoPanel
          title="Tracked keywords"
          subtitle="The Keywords page's list, with Search Console's average position for the exact query"
          flush
        >
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className={TH}>Keyword</th>
                <th className={cn(TH, 'text-right')}>Impressions</th>
                <th className={cn(TH, 'text-right')}>Clicks</th>
                <th className={cn(TH, 'text-right')}>Position</th>
              </tr>
            </thead>
            <tbody>
              {p.tracked!.map((t) => (
                <tr key={t.keyword} className={TR}>
                  <td className={cn(TD, 'text-gray-900')}>{t.keyword}</td>
                  <td className={cn(TD, 'text-right tabular-nums')}>{fmtNumber(t.impressions)}</td>
                  <td className={cn(TD, 'text-right tabular-nums')}>{fmtNumber(t.clicks)}</td>
                  <td className={cn(TD, 'text-right tabular-nums')}>
                    {t.position ?? <span className="text-xs text-gray-400">Not shown for it</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </SeoPanel>
      )}

      <SeoPanel title="Top pages" subtitle="Our pages by clicks from Google" flush>
        <RowsTable rows={p.top_pages ?? []} isPage />
      </SeoPanel>

      <SeoNote>
        {`${p.snapshot.start} to ${p.snapshot.end}, pulled ${p.snapshot.day}. `}
        Search Console's own figures for every query Google showed us for, worldwide, averaged over 28 days. The newest
        three days are left out because Search Console fills them in late.
      </SeoNote>
    </div>
  );
}

/**
 * `/admin/seo/search-console` — our real Google numbers per Search Console
 * property (ff-api service/seo_search_console.py). The Keywords page samples
 * rankings for us and competitors; this is what Google recorded for us.
 */
export default function SeoSearchConsolePage() {
  const { data, isLoading, error } = useSeoSearchConsoleQuery();
  const run = useRunSeoSearchConsole();
  const digest = useSetSeoDigest();
  const subscribed = !!data?.digest_subscribed;
  const properties = data?.properties ?? [];
  const [picked, setPicked] = useState<string | null>(null);
  const current = properties.find((p) => p.site === picked) ?? properties.find((p) => p.snapshot) ?? properties[0];

  return (
    <MarketingPageShell
      title="Search Console"
      description="What Google recorded for our own sites: the queries we showed up for, clicks, impressions and average position."
      crumbs={[{ label: 'SEO', href: '/admin/seo' }, { label: 'Search Console' }]}
      icon={MousePointerClick}
      actions={
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => digest.mutate({ subscribed: !subscribed })}
            disabled={digest.isPending || !data}
            title="A summary of these numbers by email every Monday"
          >
            {subscribed ? 'Stop the weekly email' : 'Email me weekly'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => run.mutate({})}
            disabled={run.isPending || run.isSuccess || properties.length === 0}
          >
            {run.isSuccess ? 'Queued: results in about a minute' : 'Pull now'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <SeoError>Could not load Search Console.</SeoError>}
        {(run.isError || digest.isError) && <SeoError>That did not go through. Try again.</SeoError>}
        {data?.sites_error && <SeoError>Search Console did not answer ({data.sites_error}). Showing stored snapshots.</SeoError>}

        {!isLoading && properties.length === 0 ? (
          <EmptyState label="No Search Console property is shared with our service account yet. Add the service account as a user on the property in Search Console, then pull." />
        ) : (
          <>
            {properties.length > 1 && (
              <div className="flex flex-wrap gap-2">
                {properties.map((p) => (
                  <Button
                    key={p.site}
                    size="sm"
                    variant={p.site === current?.site ? 'default' : 'outline'}
                    onClick={() => setPicked(p.site)}
                  >
                    {siteLabel(p.site)}
                  </Button>
                ))}
              </div>
            )}
            {current && <Property p={current} loading={isLoading} />}
          </>
        )}
      </div>
    </MarketingPageShell>
  );
}
